// The update gate's service-worker side: look for a new version within a time budget, and take a
// found one into use (persist first, skip waiting, reload once). Decisions: update-policy.ts.
import type { CheckResult, ReloadMarker } from './update-policy';

/** The parts of a ServiceWorkerRegistration the gate uses (tests pass a fake). */
export interface RegLike {
  waiting: { postMessage(m: unknown): void } | null;
  installing: { state: string; addEventListener(t: 'statechange', f: () => void): void } | null;
  update(): Promise<unknown>;
}

export interface CheckDeps {
  online: () => boolean;
  /** setTimeout (tests use fake timers). */
  timer?: (f: () => void, ms: number) => unknown;
}

/** Waits for an installing worker to finish: 'waiting' when it installed, 'none' when it failed. */
function settle(reg: RegLike): Promise<CheckResult> {
  const w = reg.installing;
  if (!w) return Promise.resolve(reg.waiting ? 'waiting' : 'none');
  return new Promise((resolve) => {
    const done = () => {
      if (w.state === 'installed') resolve('waiting');
      else if (w.state === 'activating' || w.state === 'activated' || w.state === 'redundant') resolve('none');
    };
    w.addEventListener('statechange', done);
    done();
  });
}

/**
 * Is there a new version? At most `budgetMs` (then 'timeout': the app goes on with what it has,
 * and the download carries on in the background for next time). Offline: 'offline' at once.
 */
export async function checkForUpdate(reg: RegLike | null | undefined, budgetMs: number, deps: CheckDeps): Promise<CheckResult> {
  if (!deps.online()) return 'offline';
  if (!reg) return 'none';
  if (reg.waiting) return 'waiting';
  const timer = deps.timer ?? ((f, ms) => setTimeout(f, ms));
  const timeout = new Promise<CheckResult>((resolve) => {
    timer(() => resolve('timeout'), budgetMs);
  });
  const check = (async (): Promise<CheckResult> => {
    try {
      await reg.update();
    } catch {
      return deps.online() ? 'error' : 'offline';
    }
    return settle(reg);
  })();
  return Promise.race([check, timeout]);
}

/**
 * A cold start that found a new version still downloading when the check's budget ran out: wait
 * for it to finish installing (at most `ms`), so it is taken into use at once behind the loading
 * screen instead of at some later idle moment (Axel 2026-10-21). 'timeout' when it is still not done.
 */
export function awaitInstall(reg: RegLike, ms: number, deps: Pick<CheckDeps, 'timer'> = {}): Promise<CheckResult> {
  if (reg.waiting) return Promise.resolve('waiting');
  if (!reg.installing) return Promise.resolve('none');
  const timer = deps.timer ?? ((f, t) => setTimeout(f, t));
  const timeout = new Promise<CheckResult>((resolve) => {
    timer(() => resolve('timeout'), ms);
  });
  return Promise.race([settle(reg), timeout]);
}

export const MARKER_KEY = 'valyrio:updateReload';

export function readMarker(storage: Pick<Storage, 'getItem'> | null): ReloadMarker | null {
  try {
    const m = JSON.parse(storage?.getItem(MARKER_KEY) ?? 'null') as ReloadMarker | null;
    return m && typeof m.build === 'string' && typeof m.at === 'number' ? m : null;
  } catch {
    return null;
  }
}

export function writeMarker(storage: Pick<Storage, 'setItem' | 'removeItem'> | null, m: ReloadMarker | null): void {
  try {
    if (m) storage?.setItem(MARKER_KEY, JSON.stringify(m));
    else storage?.removeItem(MARKER_KEY);
  } catch {
    /* private mode: no loop guard beyond this page */
  }
}

export interface ApplyDeps {
  /** Grades and the outbox: written to IndexedDB, sent if the server is reachable (bounded). */
  flush: () => Promise<void>;
  marker: (m: ReloadMarker) => void;
  build: string;
  now: () => number;
  /** Resolves when the new worker controls the page (controllerchange), or after `ms`. */
  controlled: (ms: number) => Promise<void>;
  reload: () => void;
}

/** Persist, then let the waiting worker take over (skipWaiting + clients.claim) and reload once. */
export async function applyUpdate(reg: RegLike, deps: ApplyDeps): Promise<void> {
  await deps.flush();
  const waiting = reg.waiting;
  if (!waiting) return;
  deps.marker({ build: deps.build, at: deps.now() });
  const taken = deps.controlled(3000);
  waiting.postMessage({ type: 'SKIP_WAITING' });
  await taken;
  deps.reload();
}
